/**
 * @module Viewer
 */

import { mm, Viewer as InternalViewer } from "@inovitas/infra3dsdk";
import { Layer, Tool } from "@inovitas/infra3dsdk/dist/types/mm";
import { GeoFrame } from "@inovitas/infra3dsdk/dist/types/mm/geoframe/geoframe";
import { Node as SDK_Node } from "@inovitas/infra3dsdk/dist/types/mm/graph/node";
import {
  ILatLonFov,
  IPanZoom,
} from "@inovitas/infra3dsdk/dist/types/mm/geoframe/interfaces/IPointTo";
import { transform } from "ol/proj";
import { register } from "ol/proj/proj4";
import proj4 from "proj4";

import ClientConfig from "shared/config";
import { ProviderType } from "shared/common/provider/pilegateprovider";
import { getProviderType } from "shared/common/provider/userProviderDecision";
import { clientConfig } from "shared/common/stores/defaultStore";
import {
  getApiKeyHeaders,
  getAuthorizationHeaders,
  removeItemFromArray,
  transformGeoJSON,
} from "shared/common/utils/general-utils";
import { GeometryOverlay, IGeometryOverlayOptions } from "./GeometryOverlay";
import { getUserData } from "./Login";
import { CampaignschangedEvent } from "./viewer/events/CampaignchangedEvent";
import { LookazimuthchangedEvent } from "./viewer/events/LookazimuthchangedEvent";
import { NodechangedEvent } from "./viewer/events/NodechangedEvent";
import { PanorotationchangedEvent } from "./viewer/events/PanorotationchangedEvent";
import { PanzoomchangedEvent } from "./viewer/events/PanzoomchangedEvent";
import { ViewerEventType } from "./viewer/events/ViewerEventType";
import {
  GeoJSON,
  Geometry,
  LineString,
  Point,
  Polygon,
} from "./viewer/externals/geojson";
import { IFeatureStyle } from "./viewer/interfaces/IFeatureStyle";
import { Node } from "./viewer/interfaces/INode";
import { IViewer } from "./viewer/interfaces/IViewer";
import { EventEmitter } from "./viewer/utils/EventEmitter";
import { ICampaign } from "./viewer/interfaces/ICampaign";
import { LayerShownEvent } from "./viewer/events/LayerShownEvent";
import { LayerHiddenEvent } from "./viewer/events/LayerHiddenEvent";

// Somehow proj4 needs to be registered to use
// the transform, even if it is registered in
// the general utils as well. Otherwise, the
// transform function will throw an error.
register(proj4);

/**
 * Represents the linear coordinates along a reference axis.
 */
interface LinearPosition {
  /**
   * The coordinates in the LR coordinate system.
   */
  lrCoords: LinearCoordinates;
  /**
   * The point on the axis in projected coordinates (easting, northing, height).
   */
  pointOnAxis: [number, number, number];
}

/**
 * Represents the linear coordinates along a reference axis.
 */
interface LinearCoordinates {
  /**
   * The name of the axis.
   */
  axis: string;
  /**
   * The reference point on the axis, if available.
   */
  ref_point: string | undefined;
  /**
   * The value along the axis.
   */
  u: number;
  /**
   * The lateral offset from the axis.
   */
  v: number;
  /**
   * The height above the axis.
   */
  w: number;
}

/**
 * ILayer interface represents a layer in the viewer.
 */
interface ILayer {
  /**
   * The unique identifier of the layer.
   */
  id: string;
  /**
   * The display title of the layer.
   */
  title: string;
  /**
   * The technical name of the layer.
   */
  name: string;
  /**
   * The group of the layer.
   */
  group: string;
  /**
   * A boolean indicating whether the layer is currently visible or not.
   */
  visible: boolean;
}

/**
 * Viewer Class
 *
 * The Viewer class extends the EventEmitter class and represents a wrapper around the internal viewer.
 *
 * @extends EventEmitter - Inherits functionality for event handling.
 */
export class Viewer extends EventEmitter implements IViewer {
  /**
   * @ignore
   */
  private _sdk_viewer: InternalViewer;

  /**
   * @ignore
   */
  private _geometryOverlays: GeometryOverlay[] = [];

  /**
   * @ignore
   */
  private _clientConfig: clientConfig;

  private _currentNode: Node | null = null;

  /**
   * @ignore
   */
  constructor(sdk_viewer: InternalViewer, clientConfig: clientConfig) {
    super();
    this._sdk_viewer = sdk_viewer;
    this._clientConfig = clientConfig;

    // Event listener for node changed (sadly the event is once called node and not (geo-)framechanged)
    this._sdk_viewer.on("nodechanged", (node: SDK_Node): void => {
      this._getAveragePosition(node).then((avgPos) => {
        this._currentNode = {
          id: node.frame.id,
          lat: node.latLon.lat,
          lon: node.latLon.lon,
          height: node.frame.height,
          campaign_id: node.frame.campaignID,
          date: node.frame.timestamp,
          streamtype: node.frame.streamtype,
          omega: node.frame.omega,
          phi: node.frame.phi,
          kappa: node.frame.kappa,
        };
        super.fire<NodechangedEvent>("nodechanged", {
          type: "nodechanged",
          target: this,
          avg_lat: avgPos ? avgPos.lat : 0,
          avg_lon: avgPos ? avgPos.lon : 0,
          ...this._currentNode,
        });
      });
    });

    // Event listener for campaign changed
    this._sdk_viewer.on(
      "campaignschanged",
      (data: CampaignschangedEvent[]): void => {
        const eventData = data[0];
        super.fire<CampaignschangedEvent>("campaignschanged", {
          id: eventData.id,
          project: eventData.project,
          description: eventData.description,
          type: "campaignschanged",
          target: this,
        });
      }
    );

    // Event listener for panorotation changed
    this._sdk_viewer.on("panorotationchanged", (data: any): void => {
      super.fire<PanorotationchangedEvent>("panorotationchanged", {
        type: "panorotationchanged",
        target: this,
        fov: data.fov,
        lat: data.lat,
        lon: data.lon,
      });
    });

    // Event listener for panzoom changed
    this._sdk_viewer.on("panzoomchanged", (data: any): void => {
      super.fire<PanzoomchangedEvent>("panzoomchanged", {
        type: "panzoomchanged",
        target: this,
        panX: data.panX,
        panY: data.panY,
        zoom: data.zoom,
      });
    });

    // Event listener for lookazimuth changed
    this._sdk_viewer.on("lookazimuthchanged", (value: number): void => {
      super.fire<LookazimuthchangedEvent>("lookazimuthchanged", {
        type: "lookazimuthchanged",
        target: this,
        value: value,
      });
    });

    // Event listener for layer updated
    this._sdk_viewer.on(
      "layerupdated",
      ([layer, updates]: [Layer, any]): void => {
        if (updates.visibility === undefined) return;
        const visible = updates.visibility;

        if (visible) {
          super.fire<LayerShownEvent>("layershown", {
            id: layer.id,
            title: layer.title,
            visible: visible,
            type: "layershown",
            target: this,
          });
        } else {
          super.fire<LayerHiddenEvent>("layerhidden", {
            id: layer.id,
            title: layer.title,
            visible: visible,
            type: "layerhidden",
            target: this,
          });
        }
      }
    );
  }

  /**
   * Moves the viewer to a specific campaign identified by its campaignID.
   * @param {string} campaignID - The unique identifier of the campaign to move to.
   * @returns {Promise<Node>} A Promise that resolves with the campaignID when the viewer has been successfully moved to the campaign.
   */
  public async moveToCampaign(campaignID: string): Promise<Node> {
    const node = await this._sdk_viewer.moveToCampaign(campaignID);
    return {
      id: node.frame.id,
      lat: node.latLon.lat,
      lon: node.latLon.lon,
      height: node.frame.height,
      campaign_id: node.frame.campaignID,
      date: node.frame.timestamp,
      streamtype: node.frame.streamtype,
      omega: node.frame.omega,
      phi: node.frame.phi,
      kappa: node.frame.kappa,
    };
  }

  /**
   * Moves the viewer's position to a specific image identified by its unique key.
   * @param {string} key - The unique key of the target image.
   * @returns {Promise<Node>} A Promise that resolves when the move operation is completed.
   */
  public async moveToKey(key: string): Promise<Node> {
    const node = await this._sdk_viewer.moveToKey(key);
    return {
      id: node.frame.id,
      lat: node.latLon.lat,
      lon: node.latLon.lon,
      height: node.frame.height,
      campaign_id: node.frame.campaignID,
      date: node.frame.timestamp,
      streamtype: node.frame.streamtype,
      omega: node.frame.omega,
      phi: node.frame.phi,
      kappa: node.frame.kappa,
    };
  }

  /**
   * Moves the viewer's position to a specific geographical location defined by projected XYZ coordinates.
   * @param {number} easting - The easting (horizontal) coordinate of the target location.
   * @param {number} northing - The northing (vertical) coordinate of the target location.
   * @param {number} [height] - (Deprecated) The height or elevation of the target location. Default is undefined.
   * @param {number} [maxdist] - An optional maximum distance for the move operation. Default is undefined.
   * @param {number} [epsg] - An optional EPSG code specifying the coordinate system. Default is the EPSG code of the project in the viewer.
   * @returns {Promise<Node>} A Promise that resolves when the move operation is completed.
   */
  public async moveToPosition(
    easting: number,
    northing: number,
    height?: number,
    maxdist?: number,
    epsg?: number
  ): Promise<Node> {
    const node = await this._sdk_viewer.movePosition(
      { easting, northing },
      epsg,
      maxdist
    );
    return {
      id: node.frame.id,
      lat: node.latLon.lat,
      lon: node.latLon.lon,
      height: node.frame.height,
      campaign_id: node.frame.campaignID,
      date: node.frame.timestamp,
      streamtype: node.frame.streamtype,
      omega: node.frame.omega,
      phi: node.frame.phi,
      kappa: node.frame.kappa,
    };
  }

  /**
   * Moves the viewer's position to look at a specific geographical location defined by projected XYZ coordinates.
   * @param {number} easting - The easting (horizontal) coordinate of the target location.
   * @param {number} northing - The northing (vertical) coordinate of the target location.
   * @param {number} [height] - The height or elevation of the target location. Default is undefined.
   * @param {number} [maxdist] - An optional maximum distance for the move operation. Default is undefined.
   * @param {number} [epsg] - An optional EPSG code specifying the coordinate system. Default is the EPSG code of the project in the viewer.
   * @returns {Promise<Node>} A Promise that resolves when the move operation is completed.
   */
  public async lookAtPosition(
    easting: number,
    northing: number,
    height?: number,
    maxdist?: number,
    epsg?: number
  ): Promise<Node> {
    const [node, _] = await this._sdk_viewer.movePositionLookAtPosition(
      { easting, northing, height },
      maxdist,
      epsg
    );
    return {
      id: node.frame.id,
      lat: node.latLon.lat,
      lon: node.latLon.lon,
      height: node.frame.height,
      campaign_id: node.frame.campaignID,
      date: node.frame.timestamp,
      streamtype: node.frame.streamtype,
      omega: node.frame.omega,
      phi: node.frame.phi,
      kappa: node.frame.kappa,
    };
  }

  /**
   * Moves the viewer to a specific position along a linear reference axis.
   * @param axis name of the axis, e.g. "10"
   * @param km km-value along the axis, e.g. 11.655
   * @returns
   */
  public moveToLinRefPosition(axis: string, km: number): Promise<Node> {
    return new Promise(async (resolve, reject) => {
      const tokens = {
        access_token: this._sdk_viewer.authObject.accessToken,
        expires_in: 1800,
        id_token: "",
        refresh_token: "",
        token_type: "Bearer",
      };

      if (!getUserData(tokens)?.scope.includes("addon/linref")) {
        reject(
          "The user does not have the required permission to access LinRef Features."
        );
      }

      const linrefConfig = this._clientConfig.map.geo_coders?.find(
        (geo_coder: any) => geo_coder.name === "linrefcode"
      );

      if (!linrefConfig || !linrefConfig.datasets) {
        reject("LinRef configuration not found in client config.");
        return;
      }

      const res = await fetch(
        `${ClientConfig.linrefcodeURL}/kmpoints?q=${axis} KM ${km}&limit=20&filter=dataset eq '${linrefConfig.datasets}' and line eq '${axis}'`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...getAuthorizationHeaders(this._sdk_viewer.authObject.accessToken),
            ...getApiKeyHeaders(),
          },
        }
      );

      if (!res.ok) {
        reject(`Failed to fetch LinRef data: ${res.status} ${res.statusText}`);
      }

      const data = await res.json();
      if (!data || data.value.length === 0) {
        reject("No LinRef data found for the specified axis and km.");
      }

      let point;

      // Determine the number of decimals in the km value
      let num_decimals = 5;
      if (typeof km === "number" && !Number.isInteger(km)) {
        const kmStr = km.toString();
        if (kmStr.includes(".")) {
          num_decimals = kmStr.split(".")[1].length;
        }
      }
      while (num_decimals > 0) {
        point = data.value.find(
          (p: any) => p.km.toFixed(num_decimals) === km.toFixed(num_decimals)
        );
        if (point) break;
        num_decimals--;
      }

      this._sdk_viewer
        .movePosition(
          { easting: point.longitude, northing: point.latitude },
          4326
        )
        .then((node) => {
          resolve({
            id: node.frame.id,
            lat: node.latLon.lat,
            lon: node.latLon.lon,
            height: node.frame.height,
            campaign_id: node.frame.campaignID,
            date: node.frame.timestamp,
            streamtype: node.frame.streamtype,
            omega: node.frame.omega,
            phi: node.frame.phi,
            kappa: node.frame.kappa,
          });
        })
        .catch((error) => {
          console.error("Error while moving to LinRef position:", error);
          reject(error);
        });
    });
  }

  /**
   * Retrieves route and navigation data according to the provided options.
   *
   * @param {number} [simplify=1] - The route simplification tolerance in meters.
   *   Smaller values yield more detailed routes, while larger values produce more generalized paths.
   *   Defaults to `1` (1 meter) if not specified.
   *
   * @param {Object} [bbox] - Optional bounding box to limit the returned routes.
   * @param {number} bbox.minEasting - Minimum easting coordinate of the bounding box.
   * @param {number} bbox.maxEasting - Maximum easting coordinate of the bounding box.
   * @param {number} bbox.minNorthing - Minimum northing coordinate of the bounding box.
   * @param {number} bbox.maxNorthing - Maximum northing coordinate of the bounding box.
   * @param {number} [bbox.epsg=4326] - EPSG code of the coordinate reference system. Defaults to `4326`.
   *   A very large bounding box may cause request timeouts; bounding boxes larger than 5 km × 5 km are not recommended.
   *
   * @returns {Promise<GeoJSON>} A promise that resolves to a GeoJSON object containing the route data.
   *  The GeoJSON will contain (Multi-)LineString features representing the routes. For NextGen users,
   *  the GeoJSON also includes additional properties such as `campaign_uid`, `start_timestamp` and `end_timestamp`.
   */
  public getRoutes(
    simplify: number = 1,
    bbox?: {
      minEasting: number;
      maxEasting: number;
      minNorthing: number;
      maxNorthing: number;
      epsg?: number;
    }
  ): Promise<GeoJSON> {
    return new Promise((resolve, reject) => {
      const tokens = {
        access_token: this._sdk_viewer.authObject.accessToken,
        expires_in: 1800,
        id_token: "",
        refresh_token: "",
        token_type: "Bearer",
      };
      if (
        getProviderType(getUserData(tokens).userpool_id) === ProviderType.PILE
      ) {
        this._sdk_viewer
          .getRoutes({
            segmentedonly: false,
            simplify: simplify,
          })
          .then((data) => {
            resolve(data);
          })
          .catch((error) => {
            reject(error);
          });
      } else {
        this._sdk_viewer
          .getRoutes({
            minEasting: bbox?.minEasting || -180,
            maxEasting: bbox?.maxEasting || 180,
            minNorthing: bbox?.minNorthing || -90,
            maxNorthing: bbox?.maxNorthing || 90,
            epsg: bbox?.epsg || 4326,
          })
          .then((data) => {
            resolve(data);
          })
          .catch((error) => {
            reject(error);
          });
      }
    });
  }

  /**
   * Retrieves an overview of the recorded network as lines. Only available for NextGen Users. In case of non-NextGen users an error is thrown.
   *
   * @param {number} loa - The level of abstraction (0 or 1) needs to be passed. 0 returns simplified lines, 1 returns merged and therefore more simplified lines.
   * @param {Object} bbox - Bounding box to limit the returned routes.
   * @param {number} bbox.minEasting - Minimum easting coordinate of the bounding box.
   * @param {number} bbox.maxEasting - Maximum easting coordinate of the bounding box.
   * @param {number} bbox.minNorthing - Minimum northing coordinate of the bounding box.
   * @param {number} bbox.maxNorthing - Maximum northing coordinate of the bounding box.
   * @param {number} [bbox.epsg=4326] - EPSG code of the coordinate reference system. Defaults to `4326`.
   *   A very large bounding box may cause request timeouts; bounding boxes larger than 5 km × 5 km are not recommended.
   *
   * @returns {Promise<GeoJSON>} A promise that resolves to a GeoJSON object containing the route data.
   *  The GeoJSON will contain (Multi-)LineString features representing the routes.
   *  The GeoJSON also includes additional properties such as `campaign_uids`, `start_timestamp` and `end_timestamp`.
   */
  public getRouteLines(
    loa: number,
    bbox: {
      minEasting: number;
      maxEasting: number;
      minNorthing: number;
      maxNorthing: number;
      epsg?: number;
    }
  ): Promise<GeoJSON> {
    return new Promise((resolve, reject) => {
      const tokens = {
        access_token: this._sdk_viewer.authObject.accessToken,
        expires_in: 1800,
        id_token: "",
        refresh_token: "",
        token_type: "Bearer",
      };
      if (
        getProviderType(getUserData(tokens).userpool_id) !==
        ProviderType.PILEGATE
      ) {
        reject(
          "The function getRouteLines is only supported on migrated data."
        );
      }

      this._sdk_viewer
        .getRouteLines({
          loa: loa,
          minEasting: bbox.minEasting,
          maxEasting: bbox.maxEasting,
          minNorthing: bbox.minNorthing,
          maxNorthing: bbox.maxNorthing,
          epsg: bbox.epsg || 4326,
        })
        .then((data) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * Retrieves an overview of the recorded network as a hexagon raster for further abstraction. Only available for NextGen Users. In case of non-NextGen users an error is thrown.
   *
   * @param {number} loa - The level of abstraction (0, 1 or 2). 0 returns the smallest hexes, whereas 1 and above will return even larger hexes.
   * @param {Object} bbox - Bounding box to limit the returned routes.
   * @param {number} bbox.minEasting - Minimum easting coordinate of the bounding box.
   * @param {number} bbox.maxEasting - Maximum easting coordinate of the bounding box.
   * @param {number} bbox.minNorthing - Minimum northing coordinate of the bounding box.
   * @param {number} bbox.maxNorthing - Maximum northing coordinate of the bounding box.
   * @param {number} [bbox.epsg=4326] - EPSG code of the coordinate reference system. Defaults to `4326`.
   *   A very large bounding box may cause request timeouts; bounding boxes larger than 15 km × 15 km are not recommended.
   *
   * @returns {Promise<GeoJSON>} A promise that resolves to a GeoJSON object containing the route data.
   *  The GeoJSON will contain Polygon features representing the routes.
   *  The GeoJSON also includes additional properties such as `campaign_uids`, `start_timestamp` and `end_timestamp`.
   */
  public getRouteHexes(
    loa: number,
    bbox: {
      minEasting: number;
      maxEasting: number;
      minNorthing: number;
      maxNorthing: number;
      epsg?: number;
    }
  ): Promise<GeoJSON> {
    return new Promise((resolve, reject) => {
      const tokens = {
        access_token: this._sdk_viewer.authObject.accessToken,
        expires_in: 1800,
        id_token: "",
        refresh_token: "",
        token_type: "Bearer",
      };
      if (
        getProviderType(getUserData(tokens).userpool_id) !==
        ProviderType.PILEGATE
      ) {
        reject(
          "The function getRouteHexes is only supported on migrated data."
        );
      }

      this._sdk_viewer
        .getRouteHexes({
          loa: loa,
          minEasting: bbox.minEasting,
          maxEasting: bbox.maxEasting,
          minNorthing: bbox.minNorthing,
          maxNorthing: bbox.maxNorthing,
          epsg: bbox.epsg || 4326,
        })
        .then((data) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * Retrieves all campaigns within a specified bounding box.
   * This could be used to get e.g. the name out of a campaign_uid.
   * @param {Object} bbox - The optional bounding box.
   * @param {number} bbox.minEasting - The minimum easting coordinate of the bounding box.
   * @param {number} bbox.maxEasting - The maximum easting coordinate of the bounding box.
   * @param {number} bbox.minNorthing - The minimum northing coordinate of the bounding box.
   * @param {number} bbox.maxNorthing - The maximum northing coordinate of the bounding box.
   * @param {number} bbox.epsg - The EPSG code for the coordinate system used in the bounding box.
   * @returns {Promise<ICampaign[]>} A Promise that resolves with an array of campaigns within the specified bounding box.
   */
  public getAllCampaigns(bbox?: {
    minEasting: number;
    maxEasting: number;
    minNorthing: number;
    maxNorthing: number;
    epsg: number;
  }): Promise<ICampaign[]> {
    return new Promise((resolve, reject) => {
      const tokens = {
        access_token: this._sdk_viewer.authObject.accessToken,
        expires_in: 1800,
        id_token: "",
        refresh_token: "",
        token_type: "Bearer",
      };
      if (
        getProviderType(getUserData(tokens).userpool_id) !==
        ProviderType.PILEGATE
      ) {
        reject(
          "The function getAllCampaigns is only supported on migrated data."
        );
      }

      this._sdk_viewer
        .getAllCampaigns(bbox)
        .then((data) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * Retrieves the campaign with the given campagin_uid.
   * This could be used to get e.g. the name of a campaign.
   * @param {string} campagin_uid - The campagin_uid of the campaign.
   * @returns {Promise<ICampaign>} A Promise that resolves with the campaign given by the campagin_uid.
   */
  public getCampaign(campagin_uid: string): Promise<ICampaign> {
    return new Promise((resolve, reject) => {
      const tokens = {
        access_token: this._sdk_viewer.authObject.accessToken,
        expires_in: 1800,
        id_token: "",
        refresh_token: "",
        token_type: "Bearer",
      };
      if (
        getProviderType(getUserData(tokens).userpool_id) !==
        ProviderType.PILEGATE
      ) {
        reject("The function getCampaign is only supported on migrated data.");
      }

      this._sdk_viewer
        .getCampaign(campagin_uid)
        .then((data) => {
          resolve(data);
        })
        .catch((error) => {
          reject(error);
        });
    });
  }

  /**
   * Retrieves the current linear position along the active navigation axis.
   * This function returns the coordinates in the LR coordinate system and the point on the axis.
   * If no active navigation axis is available, it logs an error message and returns undefined.
   *
   * @returns {LinearPosition | undefined} An object containing LR coordinates and the point on the axis, or undefined if no axis is available.
   */
  public getLinearPosition(): LinearPosition | undefined {
    const tokens = {
      access_token: this._sdk_viewer.authObject.accessToken,
      expires_in: 1800,
      id_token: "",
      refresh_token: "",
      token_type: "Bearer",
    };

    if (!getUserData(tokens)?.scope.includes("addon/linref")) {
      console.error(
        "The user does not have the required permission to access LinRef Features."
      );
      return;
    }

    const geoframe = this._sdk_viewer.getScene().getCurrentGeoframe();

    const axis = this._sdk_viewer
      .getScene()
      .getLRHandler()
      .getActiveNavigationAxis();
    if (!axis) {
      console.error("No navigation axis available.");
      return;
    }

    const uvw = axis.xyz2uvw(
      geoframe.easting,
      geoframe.northing,
      geoframe.height
    );
    if (!uvw) {
      return;
    }

    let ref_point: string | undefined = undefined;
    if (this._clientConfig.linref?.ref_point_attribute) {
      if (uvw.featureidx !== undefined) {
        const axisid_obj = axis.getAxisIdentifier(uvw.featureidx);
        const axisid = Object.keys(axisid_obj || {})
          .map((x) => {
            return axisid_obj[x];
          })
          .slice(-1);
        ref_point = axisid.toString();
      }
    }

    return {
      lrCoords: {
        axis: axis.name,
        ref_point: ref_point,
        u: uvw.lrcoords[0],
        v: uvw.lrcoords[1],
        w: uvw.lrcoords[2],
      },
      pointOnAxis: uvw.axpoint,
    };
  }

  attachToMeasurementTool(
    toolname: "pointMeasure",
    onmeasured: (value: undefined, geometry: Point) => void
  ): void;
  attachToMeasurementTool(
    toolname: "lengthMeasure" | "verticalDistance" | "orthogonalDistance",
    onmeasured: (value: number, geometry: LineString) => void
  ): void;
  attachToMeasurementTool(
    toolname: "areaMeasure",
    onmeasured: (value: number, geometry: Polygon) => void
  ): void;
  attachToMeasurementTool(
    toolname: "surveyWheel",
    onmeasured: (value: number, geometry: undefined) => void
  ): void;
  attachToMeasurementTool(
    toolname: "profile",
    onmeasured: (value: undefined, geometry: LineString) => void
  ): void;
  attachToMeasurementTool(
    toolname: "linRefMeasure",
    onmeasured: (value: number[], geometry: LineString) => void
  ): void;

  /**
   * Attaches a callback to a measurement tool, allowing for reaction to measurement events.
   * @param {("pointMeasure" | "lengthMeasure" | "areaMeasure")} toolname - The name of the measurement tool to attach.
   * @param {(value: number, geometry: any) => void} onmeasured - Callback function triggered when a measurement is finished.
   *                                                              Receives the measured value and geometry as parameters.
   */
  public attachToMeasurementTool<T1, T2>(
    toolname:
      | "pointMeasure"
      | "lengthMeasure"
      | "areaMeasure"
      | "verticalDistance"
      | "surveyWheel"
      | "orthogonalDistance"
      | "profile"
      | "linRefMeasure",
    onmeasured: (value: T1 | undefined, geometry: T2 | undefined) => void
  ): void {
    const selectedTool = this._sdk_viewer
      .getToolHandler()
      .getToolsByGroup("measure")
      .find((tool: any) => tool.getName() === toolname);

    if (!selectedTool) {
      console.error("Invalid Measure Tool:", toolname);
      return;
    }

    switch (toolname) {
      case "surveyWheel":
        selectedTool.on("pointset", ((
          evt: PointerEvent,
          len: string,
          toolstate: number,
          axis: mm.Axis
        ) => {
          if (toolstate !== 3) return;
          this._processMeasurementResult(toolname, len, onmeasured);
        }) as any);
        break;
      default:
        // For all other tools, ensure they are enabled
        selectedTool.on("finished", (info: any) => {
          this._processMeasurementResult(toolname, info, onmeasured);
        });
    }
  }

  // Function for processing the measurement results
  private _processMeasurementResult<T1, T2>(
    toolname: string,
    info: any,
    onmeasured: (value: T1 | undefined, geometry: T2 | undefined) => void
  ) {
    switch (toolname) {
      case "pointMeasure":
        onmeasured(
          undefined,
          JSON.parse(JSON.stringify(info.pointfeature.geometry))
        );
        break;
      case "lengthMeasure":
        onmeasured(
          info.distance,
          JSON.parse(JSON.stringify(info.linestringfeature.geometry))
        );
        break;
      case "areaMeasure":
        onmeasured(
          info.area,
          JSON.parse(JSON.stringify(info.polygonfeature.geometry))
        );
        break;
      case "verticalDistance":
        onmeasured(
          info.distance,
          JSON.parse(JSON.stringify(info.linestringfeature.geometry))
        );
        break;
      case "surveyWheel":
        onmeasured(Number(info) as T1, undefined);
        break;
      case "orthogonalDistance":
        onmeasured(
          info.distance,
          JSON.parse(JSON.stringify(info.linestringfeature.geometry))
        );
        break;
      case "profile":
        const profile_geom = {
          type: "LineString",
          coordinates: info.map((coord: any) => [coord.x, coord.y, coord.z]),
        };
        onmeasured(undefined, profile_geom as T2);
        break;
      case "linRefMeasure":
        const lr_geom = {
          type: "LineString",
          coordinates: info.aux_line,
        };
        onmeasured(info.uvwCoords.lrcoords, lr_geom as T2);
        break;
      default:
        console.error("Unknown Measure Tool:", toolname);
        return null;
    }
  }

  /**
   * Enable a tool for interactive geometry drawing.
   * @param {("Point" | "MultiPoint" | "LineString" | "Polygon")} geometryType - The geometry type. (Multi* geometry types are not supported).
   * @param options - Configuration options for the component.
   * @param {(geometry: Geometry) => void} options.onDrawProgress - Function that is continuously called during the drawing process to return the current state of the geometry.
   * @param {IFeatureStyle} options.style - Style definition of the geometry.
   * @param {AbortSignal} options.signal - An AbortSignal object instance, which can be used to abort the asynchronous operation.
   * @returns {Promise<Geometry>} Returns the drawn geometry of the specified type.
   */
  public drawGeometry(
    geometryType: "Point" | "MultiPoint" | "LineString" | "Polygon",
    options?: {
      onDrawProgress?: (geometry: Geometry) => void;
      style?: IFeatureStyle;
      signal?: AbortSignal;
    }
  ): Promise<Geometry> {
    return new Promise((resolve, reject) => {
      let tool: Tool;
      let tool_options: any;

      if (options?.signal?.aborted) {
        reject(new Error("Measurement aborted."));
        return;
      }

      switch (geometryType) {
        case "Point":
          tool_options = {
            finishedcbf: (point_evt: any) => {
              const wgs_geom = transformGeoJSON(
                point_evt.pointfeature,
                this._sdk_viewer.epsg ||
                  this._sdk_viewer.getScene().getCurrentGeoframe().srid,
                4326
              );
              if (wgs_geom.type !== "Feature") return;
              resolve(wgs_geom.geometry);
              tool.disable();
            },
            continousdrawcbf: (point_evt: any) => {
              if (options?.onDrawProgress) {
                const wgs_geom = transformGeoJSON(
                  point_evt.pointfeature,
                  this._sdk_viewer.epsg ||
                    this._sdk_viewer.getScene().getCurrentGeoframe().srid,
                  4326
                );
                if (wgs_geom.type !== "Feature") return;
                options.onDrawProgress(wgs_geom.geometry);
              }
            },
            disablecbf: () => {
              this._sdk_viewer.getScene().getToolHandler().removeTool(tool);
            },
          };
          // Workaround, because if layerstyle is set to undefined it uses another style.
          if (options?.style) {
            tool_options["layerstyle"] = new mm.FeatureStyle(options.style);
          }
          tool = this._sdk_viewer
            .getToolHandler()
            .createDrawPointTool(tool_options, "api-draw-point-geometry");
          break;
        case "LineString":
          tool_options = {
            finishedcbf: (linestring_evt: any) => {
              const wgs_geom = transformGeoJSON(
                linestring_evt.linestringfeature,
                this._sdk_viewer.epsg ||
                  this._sdk_viewer.getScene().getCurrentGeoframe().srid,
                4326
              );
              if (wgs_geom.type !== "Feature") return;
              resolve(wgs_geom.geometry);
              tool.disable();
            },
            continousdrawcbf: (linestring_evt: any) => {
              if (options?.onDrawProgress) {
                const wgs_geom = transformGeoJSON(
                  linestring_evt.linestringfeature,
                  this._sdk_viewer.epsg ||
                    this._sdk_viewer.getScene().getCurrentGeoframe().srid,
                  4326
                );
                if (wgs_geom.type !== "Feature") return;
                options.onDrawProgress(wgs_geom.geometry);
              }
            },
            disablecbf: () => {
              this._sdk_viewer.getScene().getToolHandler().removeTool(tool);
            },
          };
          // Workaround, because if layerstyle is set to undefined it uses another style.
          if (options?.style) {
            tool_options["layerstyle"] = new mm.FeatureStyle(options.style);
          }
          tool = this._sdk_viewer
            .getToolHandler()
            .createDrawLineStringTool(
              tool_options,
              "api-draw-linestring-geometry"
            );
          break;
        case "Polygon":
          tool_options = {
            finishedcbf: (polygon_evt: any) => {
              const wgs_geom = transformGeoJSON(
                polygon_evt.polygonfeature,
                this._sdk_viewer.epsg ||
                  this._sdk_viewer.getScene().getCurrentGeoframe().srid,
                4326
              );
              if (wgs_geom.type !== "Feature") return;
              resolve(wgs_geom.geometry);
              tool.disable();
            },
            continousdrawcbf: (polygon_evt: any) => {
              if (options?.onDrawProgress) {
                const wgs_geom = transformGeoJSON(
                  polygon_evt.polygonfeature,
                  this._sdk_viewer.epsg ||
                    this._sdk_viewer.getScene().getCurrentGeoframe().srid,
                  4326
                );
                if (wgs_geom.type !== "Feature") return;
                options.onDrawProgress(wgs_geom.geometry);
              }
            },
            disablecbf: () => {
              this._sdk_viewer.getScene().getToolHandler().removeTool(tool);
            },
          };
          // Workaround, because if layerstyle is set to undefined it uses another style.
          if (options?.style) {
            tool_options["layerstyle"] = new mm.FeatureStyle(options.style);
          }
          tool = this._sdk_viewer
            .getToolHandler()
            .createDrawPolygonTool(tool_options, "api-draw-polygon-geometry");
          break;
        default:
          reject(
            `Geometrytype ${geometryType} not supported by drawGeometry()!`
          );
          return;
      }

      if (tool) tool.enable();

      const abortListener = () => {
        if (tool) {
          tool.disable();
        }
        reject(new Error("Draw geometry aborted."));
        options?.signal?.removeEventListener("abort", abortListener);
      };

      options?.signal?.addEventListener("abort", abortListener);
    });
  }

  /**
   * Adds a new GeometryOverlay. A GeometryOverlay is a container for geometries to be displayed in the viewer.
   * @param {IGeometryOverlayOptions} options - Options for configuring the GeometryOverlay.
   * @returns {GeometryOverlay} Returns the added GeometryOverlay.
   */
  public addGeometryOverlay(
    options?: IGeometryOverlayOptions
  ): GeometryOverlay {
    const overlay = new GeometryOverlay(this._sdk_viewer, options);
    this._geometryOverlays.push(overlay);
    return overlay;
  }

  /**
   * Removes a previously added GeometryOverlay from the viewer. Make sure that the object reference is the same as when the GeometryOverlay was added.
   * @param {geometryoverlay} geometryoverlay - The GeometryOverlay object.
   */
  public removeGeometryOverlay(geometryoverlay: GeometryOverlay): void {
    geometryoverlay.destroy();

    removeItemFromArray(geometryoverlay, this._geometryOverlays);
  }

  /**
   * Activates a tool for interactively picking a geometry.
   * @param {AbortSignal} signal - An AbortSignal object instance, which can be used to abort the asynchronous operation.
   * @returns Returns the picked geometry and its corresponding geometry overlay.
   */
  public pickGeometry(
    signal?: AbortSignal
  ): Promise<[GeometryOverlay, Geometry]> {
    return new Promise((resolve, reject) => {
      if (signal?.aborted) {
        reject(new Error("Geometry picking aborted."));
        return;
      }

      const pick_fcs = this._geometryOverlays.map((geometryOverlay) =>
        geometryOverlay.getFeatureCollection()
      );

      const select_tool = this._sdk_viewer
        .getScene()
        .getToolHandler()
        .createSelectFeatureTool(
          {
            selectedcbf: (feature: mm.Feature) => {
              // The corresponding geom is searched and returned.
              // This ensures that the same object and not a (deep) copy of it is returned.
              this._geometryOverlays.forEach((geometryOverlay) => {
                const index = geometryOverlay
                  .getFeatures()
                  .findIndex((i) => i["infra3d_feature"] === feature);
                if (index !== -1) {
                  resolve([
                    geometryOverlay,
                    geometryOverlay.getFeatures()[index]["geojson_geometry"],
                  ]);
                }
              });

              select_tool.disable();
              this._sdk_viewer
                .getScene()
                .getToolHandler()
                .removeTool(select_tool);
            },
            hightlightonhover: true,
            activecursor: "pointer",
            selectionmodel: mm.SELECTIONMODEL.SINGLE,
            picklayer: pick_fcs,
          },
          "api-pick"
        );
      select_tool.enable();

      const abortListener = () => {
        select_tool.disable();
        this._sdk_viewer.getScene().getToolHandler().removeTool(select_tool);
        reject(new Error("Geometry picking aborted."));
        signal?.removeEventListener("abort", abortListener);
      };

      signal?.addEventListener("abort", abortListener);
    });
  }

  /**
   * @ignore
   * Activates a tool for interactively editing a geometry.
   * @param {(geom: Geometry) => void} onEditProgress  . Function that is continuously called during the editing process to return the current state of the geometry.
   * @param {AbortSignal} signal - An AbortSignal object instance, which can be used to abort the asynchronous operation.
   * @returns Returns the edited geometry and its corresponding geometry overlay.
   */
  public editGeometry(
    onEditProgress: (geom: Geometry) => void,
    signal?: AbortSignal
  ): Promise<[GeometryOverlay, Geometry]> {
    return new Promise((resolve, reject) => {
      if (signal?.aborted) {
        reject(new Error("Geometry editing aborted."));
        return;
      }

      const pick_fcs = this._geometryOverlays.map((geometryOverlay) =>
        geometryOverlay.getFeatureCollection()
      );
      const edit_tool = this._sdk_viewer
        .getScene()
        .getToolHandler()
        .createEditFeatureTool(
          {
            editedcbf: (feature: mm.Feature) => {
              // The corresponding geom is searched and returned.
              // This ensures that the same object and not a (deep) copy of it is returned.
              this._geometryOverlays.forEach((geometryOverlay) => {
                const index = geometryOverlay
                  .getFeatures()
                  .findIndex((i) => i["infra3d_feature"] === feature);
                if (index !== -1) {
                  resolve([
                    geometryOverlay,
                    geometryOverlay.getFeatures()[index]["geojson_geometry"],
                  ]);
                }
              });

              // Hack, as if we are dragging when disabling, the editedcbf is called again,
              // which results in an endless call of this function...
              setTimeout(() => {
                edit_tool.disable();
              }, 10);
            },
            continuouseditcbf: (
              coords: number[],
              pointindex: number,
              evt: any,
              edit_feature: any
            ) => {
              if (onEditProgress) {
                const wgs_geom = transformGeoJSON(
                  edit_feature,
                  this._sdk_viewer.epsg ||
                    this._sdk_viewer.getScene().getCurrentGeoframe().srid,
                  4326
                );
                if (wgs_geom.type !== "Feature") return;
                onEditProgress(wgs_geom.geometry);
              }
            },
            resetcbf: (evt: any) => {
              edit_tool.disable();
            },
            disablecbf: () => {
              this._sdk_viewer
                .getScene()
                .getToolHandler()
                .removeTool(edit_tool);
            },
            activecursor: "pointer",
            edit: true,
            picklayer: pick_fcs,
          },
          "api-edit"
        );
      edit_tool.enable();

      const abortListener = () => {
        edit_tool.disable();
        reject(new Error("Geometry editing aborted."));
        signal?.removeEventListener("abort", abortListener);
      };

      signal?.addEventListener("abort", abortListener);
    });
  }

  /**
   * Destroys the viewer instance and cleans up resources.
   * Other classes like the manager are still used. To close
   * all the infra3D JS-API resources, the `infra3dapi.destroy()`
   * function should be called.
   */
  public destroy(): void {
    this._sdk_viewer.remove();
  }

  /**
   * Sets the user interaction mode of the viewer.
   * @param {boolean} pan - If true, panning is enabled.
   * @param {boolean} zoom - If true, zooming is enabled.
   */
  public setUserInteraction(pan: boolean, zoom: boolean): void {
    this._sdk_viewer.setUserInteraction(pan, zoom);
  }

  /**
   * Gets the camera view of the viewer.
   * @return {ILatLonFov | IPanZoom} - The camera view, if the current node has streamtype mono or stereo, IPanZoom is returned, else ILatLonFov.
   */
  public getCameraView(): ILatLonFov | IPanZoom {
    return this._sdk_viewer.getCameraView();
  }

  /**
   * Sets the camera view of the viewer.
   * @param {ILatLonFov | IPanZoom} cameraView - The camera view to set. If the current node has streamtype mono or stereo, IPanZoom is expected, else ILatLonFov.
   * @param {boolean} [smooth=true] - Whether to smoothly transition to the new camera view.
   */
  public setCameraView(
    cameraView: ILatLonFov | IPanZoom,
    smooth: boolean = true
  ): void {
    this._sdk_viewer.setCameraView(cameraView, smooth);
  }

  /**
   * Gets the current node of the viewer.
   * @returns {Node | null} The current node containing geographical coordinates, height, campaign ID, date, stream type, and orientation angles, or null if no node is available.
   */
  public getCurrentNode(): Node | null {
    return this._currentNode;
  }

  /**
   * Removes an event handler for a specific event.
   * @param type - The type of the event for which the handler should be removed.
   * @param handler - The handler to be removed.
   */
  public off(type: "nodechanged", handler: (event: Node) => void): void;
  public off(
    type: "panorotationchanged",
    handler: (event: PanorotationchangedEvent) => void
  ): void;
  public off(
    type: "panzoomchanged",
    handler: (event: PanzoomchangedEvent) => void
  ): void;
  public off(
    type: "campaignschanged",
    handler: (event: CampaignschangedEvent) => void
  ): void;
  public off(
    type: "lookazimuthchanged",
    handler: (event: LookazimuthchangedEvent) => void
  ): void;
  public off(
    type: "layershown",
    handler: (event: LayerShownEvent) => void
  ): void;
  public off(
    type: "layerhidden",
    handler: (event: LayerHiddenEvent) => void
  ): void;
  public off<T>(type: ViewerEventType, handler: (event: T) => void): void {
    super.off(type, handler);
  }

  /**
   * Registers an event handler for a specific event.
   * @param type - The type of the event for which the handler should be registered.
   * @param handler - The handler to be registered.
   * @example
   * ```typescript
   * // Initialize the viewer
   * manager.initViewer({ /* initialization options * / }).then((viewer) => {
   *   // Register an event handler
   *   viewer.on("nodechanged", function(evt) {
   *     console.log("A nodechanged event has occurred.");
   *   });
   * });
   * ```
   */
  public on(
    type: "nodechanged",
    handler: (event: NodechangedEvent) => void
  ): void;
  /**
   * Registers an event handler for a specific event.
   * @param type - The type of the event for which the handler should be registered.
   * @param handler - The handler to be registered.
   * @example
   * ```typescript
   * // Initialize the viewer
   * manager.initViewer({ /* initialization options * / }).then((viewer) => {
   *   // Register an event handler
   *   viewer.on("campaignschanged", function(evt) {
   *     console.log("A campaignschanged event has occurred.");
   *   });
   * });
   * ```
   */
  public on(
    type: "campaignschanged",
    handler: (event: CampaignschangedEvent) => void
  ): void;
  /**
   * Registers an event handler for a specific event.
   * @param type - The type of the event for which the handler should be registered.
   * @param handler - The handler to be registered.
   * @example
   * ```typescript
   * // Initialize the viewer
   * manager.initViewer({ /* initialization options * / }).then((viewer) => {
   *   // Register an event handler
   *   viewer.on("panorotationchanged", function(evt) {
   *     console.log("A panorotationchanged event has occurred.");
   *   });
   * });
   * ```
   */
  public on(
    type: "panorotationchanged",
    handler: (event: PanorotationchangedEvent) => void
  ): void;
  /**
   * Registers an event handler for a specific event.
   * @param type - The type of the event for which the handler should be registered.
   * @param handler - The handler to be registered.
   * @example
   * ```typescript
   * // Initialize the viewer
   * manager.initViewer({ /* initialization options * / }).then((viewer) => {
   *   // Register an event handler
   *   viewer.on("lookazimuthchanged", function(evt) {
   *     console.log("A lookazimuthchanged event has occurred.");
   *   });
   * });
   * ```
   */
  public on(
    type: "lookazimuthchanged",
    handler: (event: LookazimuthchangedEvent) => void
  ): void;
  /**
   * Registers an event handler for a specific event.
   * @param type - The type of the event for which the handler should be registered.
   * @param handler - The handler to be registered.
   * @example
   * ```typescript
   * // Initialize the viewer
   * manager.initViewer({ /* initialization options * / }).then((viewer) => {
   *   // Register an event handler
   *   viewer.on("layershown", function(evt) {
   *     console.log("A layershown event has occurred.");
   *   });
   * });
   * ```
   */
  public on(
    type: "layershown",
    handler: (event: LayerShownEvent) => void
  ): void;
  /**
   * Registers an event handler for a specific event.
   * @param type - The type of the event for which the handler should be registered.
   * @param handler - The handler to be registered.
   * @example
   * ```typescript
   * // Initialize the viewer
   * manager.initViewer({ /* initialization options * / }).then((viewer) => {
   *   // Register an event handler
   *   viewer.on("layerhidden", function(evt) {
   *     console.log("A layerhidden event has occurred.");
   *   });
   * });
   * ```
   */
  public on(
    type: "layerhidden",
    handler: (event: LayerHiddenEvent) => void
  ): void;
  public on<T>(type: ViewerEventType, handler: (event: T) => void): void {
    super.on(type, handler);
  }

  /**
   * Calculates the average position (lat/lon) from a given array of nodes.
   * @param {Node[]} current_node - The node to calculate the average from.
   * @returns {{lat: number, lon: number} | undefined} The calculated average latitude and longitude, or undefined if the input array is empty.
   */
  private _getAveragePosition(
    current_node: SDK_Node
  ): Promise<{ lat: number; lon: number } | undefined> {
    return new Promise((resolve) => {
      this._sdk_viewer
        .getScene()
        .navigator.cameraService.cacheCameras$(current_node)
        .subscribe((nodes) => {
          if (!nodes || nodes.length === 0) {
            resolve(undefined);
            return;
          }

          const eastings = nodes.map((node) => node.latLon.lon);
          const average_easting = eastings.reduce(
            (avg, value, _, { length }) => {
              return avg + value / length;
            },
            0
          );

          const northings = nodes.map((node) => node.latLon.lat);
          const average_northing = northings.reduce(
            (avg, value, _, { length }) => {
              return avg + value / length;
            },
            0
          );

          resolve({ lat: average_northing, lon: average_easting });
        });
    });
  }

  /**
   * Get all the layers in infra3d.
   * @returns {ILayer[]} Returns all layers that are present in infra3d.
   */
  public getLayers(): ILayer[] {
    const layers = this._sdk_viewer.getScene().getLayerHandler().layers;
    const mappedLayers = layers
      .filter((layer) => layer.type !== mm.LAYERTYPE.NETWORKLAYER)
      .map((layer) => {
        return {
          id: layer.id,
          title: layer.title,
          name: layer.layername,
          group: layer.topic,
          visible: layer.getVisible(),
        };
      });
    return mappedLayers;
  }

  /**
   * Shows a specific layer in infra3d.
   * @param {string} layerId - The unique identifier of the layer to show.
   * @returns
   * */
  public showLayerById(layerId: string) {
    this._sdk_viewer.getScene().getLayerHandler().showLayerById(layerId);
  }

  /**
   * Hides a specific layer in infra3d.
   * @param {string} layerId - The unique identifier of the layer to hide.
   * @returns
   */
  public hideLayerById(layerId: string) {
    this._sdk_viewer.getScene().getLayerHandler().hideLayerById(layerId);
  }
}
